import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Cases() {
  useEffect(() => {
    // SEO Meta tags for Cases page
    document.title = '実施事例 | 株式会社OnYourSide';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '様々な業界・規模の企業様や個人の方々に研修・コンサルティングをご提供し、確実な成果を実現した実施事例をご紹介します。');
    }

    // Schema.org JSON-LD for Cases page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "実施事例",
      "description": "様々な業界・規模の企業様や個人の方々に研修・コンサルティングをご提供し、確実な成果を実現しています。",
      "url": `${import.meta.env.VITE_SITE_URL}/cases`,
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": [
          {
            "@type": "CaseStudy",
            "name": "ダイバーシティ推進プログラム導入",
            "description": "IT企業でのダイバーシティ推進により従業員エンゲージメントが向上"
          },
          {
            "@type": "CaseStudy", 
            "name": "コンプライアンス研修プログラム実施",
            "description": "金融機関でのコンプライアンス意識向上研修"
          }
        ]
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const cases = [
    {
      id: 1,
      category: 'コンサルティング',
      title: 'ダイバーシティ推進プログラム導入',
      company: 'IT企業F社（従業員数：200名）',
      challenge: '男性中心の組織文化で女性管理職比率が低く、多様性の活用ができていませんでした。',
      solution: 'ダイバーシティ戦略の策定、管理職向け研修、働き方改革の推進、評価制度の見直しを実施。',
      result: '従業員エンゲージメントスコアが65%から85%に向上。女性管理職比率が15%から30%に増加し、チーム内の心理的安全性が大幅に改善。多様な視点による新規事業提案が40%増加し、イノベーション創出が活性化されました。',
      duration: '15ヶ月間',
      participants: '全従業員・特に管理職層',
      image: 'https://public.readdy.ai/ai/img_res/6baf169b9d5ac5e6709b7e85a4081020.jpg'
    },
    {
      id: 2,
      category: '企業研修',
      title: 'コンプライアンス研修プログラム実施',
      company: '金融機関A社（従業員数：200名）',
      challenge: '金融業界の規制強化に伴い、全社的なコンプライアンス意識の向上と法令遵守体制の強化が急務でした。',
      solution: '3.5時間のコンプライアンス研修を管理職・役員向けに実施。ケーススタディとグループディスカッションを組み合わせたプログラムを提供。',
      result: '参加者の98%がコンプライアンス理解度向上を実感。社内のリスク管理体制が大幅に改善されました。',
      duration: '3.5時間',
      participants: '管理職・役員25名',
      image: 'https://readdy.ai/api/search-image?query=Professional%20compliance%20training%20session%20in%20modern%20conference%20room%2C%20group%20of%20Japanese%20financial%20institution%20executives%20and%20managers%20engaged%20in%20legal%20compliance%20workshop%2C%20Japanese%20female%20trainer%20presenting%2C%20collaborative%20learning%20environment%20with%20presentation%20screen%20and%20training%20materials%2C%20Japanese%20participants%20only&width=600&height=400&seq=casej1&orientation=landscape'
    },
    {
      id: 3,
      category: '企業研修',
      title: 'ハラスメントにならない指導研修',
      company: '製造業B社（従業員数：150名）',
      challenge: '職場でのハラスメント防止と適切な指導方法の確立が課題となっていました。管理職の指導スキル向上が必要でした。',
      solution: '4時間のハラスメント防止・適切な指導研修を管理職向けに実施。ロールプレイングと事例検討を中心としたプログラムを提供。',
      result: '管理職の95%が適切な指導方法を習得。職場の心理的安全性が向上し、従業員満足度も改善されました。',
      duration: '4時間',
      participants: '管理職20名',
      image: 'https://readdy.ai/api/search-image?query=Manufacturing%20company%20harassment%20prevention%20training%20workshop%2C%20diverse%20group%20of%20Japanese%20manufacturing%20managers%20participating%20in%20appropriate%20guidance%20training%2C%20industrial%20training%20facility%20in%20Japan%2C%20professional%20development%20session%20with%20role-playing%20exercises%2C%20Japanese%20participants%20only&width=600&height=400&seq=casej3&orientation=landscape'
    },
    {
      id: 4,
      category: 'コンサルティング',
      title: '地方自治体外郭団体での人事制度改革',
      company: '地方自治体外郭団体',
      challenge: '組織の高齢化と人材不足、職員のモチベーション低下が深刻な課題でした。',
      solution: '現状分析から始まり、新しい人事評価制度の設計、職員研修プログラムの構築、組織風土改革を段階的に実施。',
      result: '職員満足度が40%向上、離職率が半減。新制度により公平で透明性の高い評価システムを構築しました。',
      duration: '12ヶ月間',
      participants: '全職員対象',
      image: 'https://readdy.ai/api/search-image?query=Government%20organization%20consulting%20meeting%2C%20Japanese%20public%20sector%20professionals%20discussing%20HR%20reform%20in%20government%20office%20conference%20room%2C%20diverse%20group%20of%20Japanese%20civil%20servants%20with%20consultant%2C%20official%20government%20building%20setting%20in%20Japan%2C%20professional%20consultation%20atmosphere%2C%20Japanese%20participants%20only&width=600&height=400&seq=consultj1&orientation=landscape'
    },
    {
      id: 5,
      category: 'コンサルティング',
      title: '中小企業での採用戦略構築支援',
      company: 'サービス業D社（従業員数：45名）',
      challenge: '優秀な人材の採用が困難で、慢性的な人材不足に悩まされていました。',
      solution: '採用ブランディング戦略の策定、面接プロセスの改善、社内研修制度の構築を包括的に支援。',
      result: '応募者数が3倍に増加、採用成功率も60%向上。定着率の改善も実現しました。',
      duration: '8ヶ月間',
      participants: '経営陣・人事担当者',
      image: 'https://readdy.ai/api/search-image?query=Small%20business%20HR%20consulting%20session%2C%20Japanese%20small%20company%20management%20team%20meeting%20with%20HR%20consultant%20about%20recruitment%20strategy%2C%20modern%20small%20office%20environment%20with%20charts%20and%20planning%20materials%2C%20diverse%20Japanese%20business%20professionals%20only&width=600&height=400&seq=consultj2&orientation=landscape'
    }
  ];

  const stats = [
    { number: '98%', label: '研修満足度' },
    { number: '300+', label: '実施実績' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            実施事例
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            様々な業界・規模の企業様や個人の方々に研修・コンサルティングをご提供し、確実な成果を実現しています
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2">
            <button className="px-4 py-2 rounded-full bg-blue-600 text-white font-medium">
              全て
            </button>
            <button className="px-4 py-2 rounded-full bg-gray-100 text-gray-700 hover:bg-blue-100 font-medium">
              企業研修
            </button>
            <button className="px-4 py-2 rounded-full bg-gray-100 text-gray-700 hover:bg-blue-100 font-medium">
              コンサルティング
            </button>
            <button className="px-4 py-2 rounded-full bg-gray-100 text-gray-700 hover:bg-blue-100 font-medium">
              カスタム研修
            </button>
          </div>
        </div>
      </section>

      {/* Cases Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {cases.map((case_item, index) => (
              <div key={case_item.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-4 ${
                    case_item.category === 'コンサルティング' 
                      ? 'bg-blue-100 text-blue-800'
                      : case_item.category === '企業研修'
                      ? 'bg-blue-100 text-blue-800'
                      : case_item.category === '個人講座'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {case_item.category}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {case_item.title}
                  </h2>
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="font-semibold text-gray-900 mb-2">実施概要</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>対象：</strong>{case_item.company}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>期間：</strong>{case_item.duration}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>参加者：</strong>{case_item.participants}
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <i className="ri-error-warning-line text-orange-500 mr-2"></i>
                        課題
                      </h4>
                      <p className="text-gray-600 leading-relaxed">
                        {case_item.challenge}
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <i className="ri-lightbulb-line text-blue-500 mr-2"></i>
                        解決策
                      </h4>
                      <p className="text-gray-600 leading-relaxed">
                        {case_item.solution}
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <i className="ri-trophy-line text-green-500 mr-2"></i>
                        成果
                      </h4>
                      <p className="text-gray-600 leading-relaxed">
                        {case_item.result}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <img
                    src={case_item.image}
                    alt={case_item.title}
                    className="rounded-xl shadow-lg object-cover w-full h-96"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              対応業界
            </h2>
            <p className="text-xl text-gray-600">
              様々な業界での豊富な研修・コンサルティング実績があります
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
            {[
              { name: 'IT・テクノロジー', icon: 'ri-computer-line' },
              { name: '製造業', icon: 'ri-settings-3-line' },
              { name: '医療・介護', icon: 'ri-hospital-line' },
              { name: '小売・サービス', icon: 'ri-store-2-line' },
              { name: '金融・保険', icon: 'ri-bank-line' },
              { name: '教育・研修', icon: 'ri-graduation-cap-line' },
              { name: '建設・不動産', icon: 'ri-building-line' },
              { name: '運輸・物流', icon: 'ri-truck-line' },
              { name: '官公庁', icon: 'ri-government-line' },
              { name: 'NPO・NGO', icon: 'ri-community-line' },
              { name: '飲食業', icon: 'ri-restaurant-line' },
              { name: 'その他', icon: 'ri-more-line' }
            ].map((industry, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-lg shadow-md mb-3 mx-auto">
                  <i className={`${industry.icon} text-2xl text-blue-600`}></i>
                </div>
                <p className="text-sm text-gray-700 font-medium">{industry.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            あなたの組織も変革を始めませんか？
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            これらの成功事例のように、あなたの組織の課題解決をサポートいたします
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 whitespace-nowrap">
                無料相談を申し込む
              </Button>
            </Link>
            <div className="flex gap-4">
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 whitespace-nowrap">
                  研修プログラム
                </Button>
              </Link>
              <Link to="/hr-consulting">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 whitespace-nowrap">
                  コンサルティング
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
